import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { ROLES, ALL_SUBJECTS, DAYS, PPPD_TYPES, PPPD_CATEGORIES, EXAM_TYPES, BRANCHES, BRANCH_QCOUNTS, ETUT_OPTIONS } from "@/lib/constants";
import ExamRanking from "@/components/ExamRanking";
import StudentPicker from "@/components/StudentPicker";
import StudentWorkspace, { WORKSPACE_TABS } from "@/components/StudentWorkspace";
import OpticalExams from "@/features/OpticalExams";
import TrackingCard from "@/features/TrackingCard";
import ExamCalendar from "@/features/ExamCalendar";
import ResourceLibrary from "@/features/ResourceLibrary";
import ClassReport from "@/features/ClassReport";
import PublisherReport from "@/features/PublisherReport";
import ProgramTemplates from "@/features/ProgramTemplates";
import {
  Users, Plus, Trash2, Loader2, Pencil, X, Trophy, FileUp, Bot, Send, Medal, ClipboardCheck, LayoutDashboard, ScanLine,
  CalendarDays, CalendarCheck, ListChecks, BookOpen, GitBranch, Brain, AlertTriangle, FlaskConical, BarChart3, Target, GraduationCap, Download, Upload, TrendingUp, Layers,
} from "lucide-react";
import { DatabaseBackup, Building2, FileBadge } from "lucide-react";
import BackupsModal from "@/features/BackupsModal";
import ReportCards from "@/features/ReportCards";
import InstitutionModal from "@/features/InstitutionModal";

const NAV = [
  { id: "users", label: "Kullanıcılar", icon: Users },
  { id: "panels", label: "Öğrenci Panelleri", icon: LayoutDashboard },
  { id: "tracking", label: "Takip Kartı", icon: ClipboardCheck },
  { id: "library", label: "Kaynak Kütüphanesi", icon: BookOpen },
  { id: "weeklyprog", label: "Haftalık Program", icon: CalendarCheck },
  { id: "exams", label: "Sınav Takvimi", icon: CalendarDays },
  { id: "ranking", label: "Haftalık Sıralama", icon: Trophy },
  { id: "examrank", label: "Deneme Başarı Tablosu", icon: Medal },
  { id: "classreport", label: "Sınıf Raporu", icon: GraduationCap },
  { id: "pubreport", label: "Yayınevi Raporu", icon: BookOpen },
  { id: "optical", label: "Canlı Sınav (Optik)", icon: ScanLine },
  { id: "karne", label: "Karne & Belgeler", icon: FileBadge },
  { id: "ai", label: "AI Asistan", icon: Bot },
];

const ENTRY_NAV = [
  { id: "study", label: "Çalışma Programı", icon: CalendarCheck },
  { id: "daily", label: "Günlük Soru", icon: ListChecks },
  { id: "resources", label: "Kaynak Takibi", icon: BookOpen },
  { id: "units", label: "Ünite / Konu", icon: GitBranch },
  { id: "pppd", label: "PPD Takibi", icon: Brain },
  { id: "weak", label: "Zayıf Konular", icon: AlertTriangle },
  { id: "branch", label: "Branş Deneme", icon: FlaskConical },
  { id: "general", label: "Genel Deneme", icon: BarChart3 },
  { id: "targets", label: "Hedef Net", icon: Target },
];

const EMPTY = { email: "", password: "", name: "", role: "student", track: "", coach_id: "", parent_id: "", phone: "", il: "", ilce: "", contact_email: "", class_no: "", sube: "" };

export default function AdminPanel() {
  const [active, setActive] = useState("users");
  const [entryTab, setEntryTab] = useState("");
  return (
    <Shell subtitle="Yönetici" nav={NAV} active={active} setActive={(id) => { setActive(id); setEntryTab(""); }}
      secondNav={ENTRY_NAV} secondActive={entryTab} setSecondActive={(id) => { setEntryTab(id); setActive(""); }}>
      {entryTab ? (
        <StudentDataEntry module={entryTab} />
      ) : (
        <>
          {active === "users" && <UsersSection />}
          {active === "panels" && <AdminStudentPanels />}
          {active === "tracking" && <AdminTrackingSection />}
          {active === "library" && <ResourceLibrary />}
          {active === "weeklyprog" && <WeeklyProgramPanel />}
          {active === "exams" && <ExamCalendar />}
          {active === "ranking" && <RankingSection />}
          {active === "examrank" && <ExamRanking />}
          {active === "classreport" && <ClassReport />}
          {active === "pubreport" && <PublisherReport />}
          {active === "optical" && <OpticalExams />}
          {active === "karne" && <ReportCards />}
          {active === "ai" && <AiSection />}
        </>
      )}
    </Shell>
  );
}

function AdminStudentPanels() {
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("overview");

  if (selected) {
    return (
      <div className="space-y-4" data-testid="admin-student-panel">
        <button onClick={() => setSelected(null)} className="text-sm font-semibold text-indigo-600 hover:text-indigo-800" data-testid="panels-back">
          ← Öğrenci Listesi
        </button>
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{selected.name}</h1>
          <div className="text-sm text-slate-500">{selected.track || "Alan yok"} · tam yetki (düzenleme / silme)</div>
        </div>
        <div className="card-ta p-2 flex flex-wrap gap-1.5">
          {WORKSPACE_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`panels-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
        <StudentWorkspace studentId={selected.id} studentName={selected.name} track={selected.track} readOnly={false} tab={tab} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-panels">
      <div>
        <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><LayoutDashboard size={13} /> Direkt Geçiş</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mt-1">Öğrenci Panelleri</h1>
        <p className="text-slate-500 text-sm mt-1">Öğrenci seçin — panelini tüm sekmeleriyle görüntüleyin, düzenleyin veya kayıt silin.</p>
      </div>
      <StudentPicker selected={selected} onSelect={(s) => { setSelected(s); setTab("overview"); }} />
    </div>
  );
}

function WeeklyProgramPanel() {
  const [sub, setSub] = useState("templates");
  return (
    <div className="space-y-5" data-testid="weekly-program-panel">
      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-2">
        <button onClick={() => setSub("templates")} data-testid="wp-tab-templates"
          className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${sub === "templates" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
          Program Şablonları
        </button>
        <button onClick={() => setSub("entry")} data-testid="wp-tab-entry"
          className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${sub === "entry" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
          Manuel Veri Girişi
        </button>
      </div>
      {sub === "templates" ? <ProgramTemplates /> : <StudentDataEntry module="schedule" />}
    </div>
  );
}

function AdminTrackingSection() {
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  return (
    <div className="space-y-5" data-testid="admin-tracking-section">
      <div>
        <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><ClipboardCheck size={13} /> Takip Kartı</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mt-1">Takip Kartı</h1>
        <p className="text-slate-500 text-sm mt-1">Öğrenci seçin; özel ders, konu pekiştirme, soru çözüm ve kütüphane kayıtlarını girip onaylayın.</p>
      </div>
      <div className="card-ta p-4 flex items-center gap-3">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 shrink-0"><Users size={16} /> Öğrenci</span>
        <select className="ta-input max-w-md" value={sid} onChange={(e) => setSid(e.target.value)} data-testid="tracking-student">
          <option value="">Öğrenci seç</option>
          {students.map((s) => <option key={s.id} value={s.id}>{s.name}{s.track ? ` · ${s.track}` : ""}</option>)}
        </select>
      </div>
      {sid ? (
        <TrackingCard studentId={sid} readOnly={false} />
      ) : (
        <div className="card-ta p-12 text-center text-slate-500">Takip kartını görüntülemek için öğrenci seçin.</div>
      )}
    </div>
  );
}

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [parents, setParents] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("student");
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, c, p, meta] = await Promise.all([
        api.get("/users"),
        api.get("/users?role=coach"),
        api.get("/users?role=parent"),
        api.get("/meta"),
      ]);
      setUsers(all.data.filter((u) => u.role !== "admin"));
      setCoaches(c.data);
      setParents(p.data);
      setTracks(meta.data.tracks);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, role: roleFilter });
    setDialog(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...EMPTY, ...u, password: "" });
    setDialog(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.coach_id === "") payload.coach_id = null;
      if (payload.parent_id === "") payload.parent_id = null;
      if (payload.track === "") payload.track = null;
      if (editing) {
        if (!payload.password) delete payload.password;
        delete payload.email;
        delete payload.role;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Kullanıcı güncellendi");
      } else {
        const { data } = await api.post("/users", payload);
        if (data.generated_password) {
          setCreated({ name: data.name, username: data.username, password: data.generated_password });
        } else {
          toast.success("Kullanıcı oluşturuldu");
        }
      }
      setDialog(false);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu kullanıcı silinsin mi?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const resetPw = async (id) => {
    try {
      const { data } = await api.post(`/users/${id}/reset-password`);
      toast.success(`Yeni şifre üretildi: ${data.password}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const filtered = users.filter((u) => u.role === roleFilter);
  const [selIds, setSelIds] = useState([]);
  const [bulkCls, setBulkCls] = useState({ class_no: "", sube: "" });
  const toggleSel = (id) => setSelIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = filtered.length > 0 && filtered.every((u) => selIds.includes(u.id));
  const applyBulkCls = async () => {
    if (!bulkCls.class_no && !bulkCls.sube) return toast.error("Sınıf veya şube girin");
    try {
      const body = { student_ids: selIds };
      if (bulkCls.class_no) body.class_no = bulkCls.class_no;
      if (bulkCls.sube) body.sube = bulkCls.sube;
      const { data } = await api.post("/users/bulk-class", body);
      toast.success(`${data.updated} öğrencinin sınıf/şubesi güncellendi`);
      setSelIds([]); setBulkCls({ class_no: "", sube: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const [showBackups, setShowBackups] = useState(false);
  const [showInstitution, setShowInstitution] = useState(false);

  const downloadBackup = async () => {
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `takip_yedek_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Veritabanı yedeği indirildi");
    } catch (e) {
      toast.error("Yedek indirilemedi: " + apiErr(e));
    }
  };

  const uploadBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setLoading(true);
    try {
      await api.post("/backup/import", form);
      toast.success("Yedek başarıyla yüklendi! Veriler yenilendi.");
      load();
    } catch (err) {
      toast.error("Yedek yükleme hatası: " + apiErr(err));
      setLoading(false);
    }
  };

  const deleteAllStudents = async () => {
    if (!window.confirm("TÜM öğrenciler ve program/sonuç kayıtları silinecek. Emin misiniz?")) return;
    if (!window.confirm("Bu işlem geri alınamaz! Devam edilsin mi?")) return;
    try {
      const { data } = await api.delete("/users/students/all");
      toast.success(`${data.deleted} öğrenci ve tüm kayıtları silindi`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Kullanıcı Yönetimi</h1>
          <p className="text-slate-500 text-sm mt-1">Öğrenci, koç ve veli hesaplarını oluştur ve yönet.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showBackups && <BackupsModal onClose={() => setShowBackups(false)} />}
          {showInstitution && <InstitutionModal onClose={() => setShowInstitution(false)} />}
          <Button variant="outline" size="sm" onClick={() => setShowInstitution(true)} className="border-pink-300 text-pink-700" data-testid="institution-btn" title="Belgelerde görünecek kurum logosu ve adı">
            <Building2 size={15} /> <span className="ml-1">Kurum Logosu</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowBackups(true)} className="border-emerald-300 text-emerald-700" data-testid="auto-backups-btn" title="Gece otomatik alınan yedekler">
            <DatabaseBackup size={15} /> <span className="ml-1">Otomatik Yedekler</span>
          </Button>
          <Button variant="outline" size="sm" onClick={downloadBackup} className="border-slate-300 text-slate-700" data-testid="backup-export-btn" title="Tüm veritabanı yedeğini JSON olarak indir">
            <Download size={15} /> <span className="ml-1">Yedek İndir</span>
          </Button>
          <label className="cursor-pointer inline-flex items-center justify-center px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium transition-colors" title="JSON yedeğini veritabanına aktar">
            <Upload size={15} /> <span className="ml-1">Yedek Yükle</span>
            <input type="file" accept=".json" onChange={uploadBackup} className="hidden" data-testid="backup-import-input" />
          </label>
          <Button variant="outline" size="sm" onClick={deleteAllStudents} className="border-rose-300 text-rose-600 hover:bg-rose-50" data-testid="delete-all-students-btn" title="Tüm öğrencileri ve tüm kayıtlarını kalıcı siler">
            <Trash2 size={15} /> <span className="ml-1">Tüm Öğrencileri Sil</span>
          </Button>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-user-btn">
            <Plus size={16} /> <span className="ml-1">Yeni Kullanıcı</span>
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        {["student", "coach", "parent"].map((r) => (
          <button
            key={r}
            onClick={() => setRoleFilter(r)}
            data-testid={`filter-${r}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
              roleFilter === r ? ROLES[r].badge : "border-slate-300 text-slate-500 hover:text-slate-800"
            }`}
          >
            {ROLES[r].label} ({users.filter((u) => u.role === r).length})
          </button>
        ))}
      </div>

      <div className="card-ta overflow-hidden">
        {roleFilter === "student" && selIds.length > 0 && (
          <div className="flex flex-wrap items-end gap-3 px-4 py-3 bg-indigo-50 border-b border-indigo-100" data-testid="bulk-class-bar">
            <div className="text-sm font-bold text-indigo-900 pb-2">{selIds.length} öğrenci seçildi →</div>
            <div><label className="ta-label">Sınıf</label><input className="ta-input w-24" placeholder="12" value={bulkCls.class_no} onChange={(e) => setBulkCls({ ...bulkCls, class_no: e.target.value })} data-testid="bulk-class-no" /></div>
            <div><label className="ta-label">Şube</label><input className="ta-input w-24" placeholder="A" value={bulkCls.sube} onChange={(e) => setBulkCls({ ...bulkCls, sube: e.target.value.toUpperCase() })} data-testid="bulk-sube" /></div>
            <Button onClick={applyBulkCls} className="bg-indigo-900 hover:bg-indigo-800 h-10" data-testid="bulk-class-apply">Toplu Ata</Button>
            <Button variant="outline" onClick={() => setSelIds([])} className="h-10" data-testid="bulk-class-clear">Seçimi Kaldır</Button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500">
                {roleFilter === "student" && <th className="px-3 py-3"><input type="checkbox" checked={allSel} onChange={() => setSelIds(allSel ? [] : filtered.map((u) => u.id))} data-testid="select-all-students" /></th>}
                <th className="px-4 py-3 font-medium">Ad</th>
                <th className="px-4 py-3 font-medium">Kullanıcı Adı</th>
                <th className="px-4 py-3 font-medium">Şifre</th>
                <th className="px-4 py-3 font-medium">Telefon</th>
                {roleFilter === "student" && <th className="px-4 py-3 font-medium">Alan</th>}
                {roleFilter === "student" && <th className="px-4 py-3 font-medium">Sınıf/Şube</th>}
                {roleFilter === "student" && <th className="px-4 py-3 font-medium">Koç</th>}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody data-testid="users-table">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={20} /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">Kayıt yok.</td></tr>
              ) : (
                filtered.map((u) => (
                  <tr key={u.id} className={`border-b border-slate-200 hover:bg-slate-50 ${selIds.includes(u.id) ? "bg-indigo-50/60" : ""}`}>
                    {roleFilter === "student" && <td className="px-3 py-3"><input type="checkbox" checked={selIds.includes(u.id)} onChange={() => toggleSel(u.id)} data-testid={`select-student-${u.id}`} /></td>}
                    <td className="px-4 py-3 text-slate-900 font-medium">{u.name}</td>
                    <td className="px-4 py-3 text-slate-500">{u.email}</td>
                    <td className="px-4 py-3" data-testid={`user-pass-${u.id}`}>
                      {u.plain_password ? (
                        <span className="font-mono font-semibold text-slate-800">{u.plain_password}</span>
                      ) : (
                        <button onClick={() => resetPw(u.id)} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid={`reset-pass-${u.id}`}>Şifre Üret</button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600" data-testid={`user-phone-${u.id}`}>{u.phone || "-"}</td>
                    {roleFilter === "student" && <td className="px-4 py-3 text-slate-600">{u.track || "-"}</td>}
                    {roleFilter === "student" && <td className="px-4 py-3 text-slate-600" data-testid={`user-class-${u.id}`}>{[u.class_no, u.sube].filter(Boolean).join(" / ") || "-"}</td>}
                    {roleFilter === "student" && <td className="px-4 py-3 text-slate-600">{coaches.find((c) => c.id === u.coach_id)?.name || "-"}</td>}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(u)} className="text-slate-500 hover:text-blue-600 mr-3" data-testid={`edit-${u.id}`}><Pencil size={16} /></button>
                      <button onClick={() => remove(u.id)} className="text-slate-500 hover:text-rose-600" data-testid={`del-user-${u.id}`}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {created && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="created-modal">
          <div className="absolute inset-0 bg-black/70" onClick={() => setCreated(null)} />
          <div className="relative card-ta w-full max-w-md p-6 space-y-4 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Plus className="text-emerald-600" size={28} />
            </div>
            <h3 className="font-display text-xl font-extrabold text-slate-900">Hesap Oluşturuldu</h3>
            <p className="text-sm text-slate-500">{created.name} için giriş bilgileri. Öğrenci/koç bunlarla giriş yapar — lütfen not alın.</p>
            <div className="space-y-2 text-left">
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Kullanıcı Adı</span>
                <span className="font-mono font-bold text-slate-900" data-testid="created-username">{created.username}</span>
              </div>
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Şifre</span>
                <span className="font-mono font-bold text-slate-900" data-testid="created-password">{created.password}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 border-slate-300" onClick={() => { navigator.clipboard?.writeText(`Kullanıcı: ${created.username}  Şifre: ${created.password}`); toast.success("Kopyalandı"); }} data-testid="created-copy">Kopyala</Button>
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => setCreated(null)} data-testid="created-close">Tamam</Button>
            </div>
          </div>
        </div>
      )}


      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDialog(false)} />
          <form onSubmit={save} className="relative card-ta w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold">{editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</h3>
              <button type="button" onClick={() => setDialog(false)}><X className="text-slate-500" size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="ta-label">Ad Soyad</label>
                <input className="ta-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="uf-name" />
              </div>
              <div>
                <label className="ta-label">Kullanıcı Adı / E-posta (opsiyonel — boşsa otomatik kısa kullanıcı adı)</label>
                <input className="ta-input" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Boş bırak → örn. ogr1234" data-testid="uf-email" />
              </div>
              <div>
                <label className="ta-label">Şifre {editing && "(boş=değişmez)"}</label>
                <input className="ta-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Değiştirmek için yaz" : "Boş bırak → otomatik 6 haneli"} data-testid="uf-password" />
              </div>
              <div>
                <label className="ta-label">Rol</label>
                <select className="ta-input" value={form.role} disabled={!!editing} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="uf-role">
                  <option value="student">Öğrenci</option>
                  <option value="coach">Eğitim Koçu</option>
                  <option value="parent">Veli</option>
                </select>
              </div>
              <div>
                <label className="ta-label">Telefon</label>
                <input className="ta-input" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="uf-phone" />
              </div>
              {form.role === "parent" && (
                <div className="col-span-2">
                  <label className="ta-label">Veli E-posta (bildirimler için)</label>
                  <input type="email" className="ta-input" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="veli@ornek.com" data-testid="uf-contact-email" />
                  <p className="text-[11px] text-slate-400 mt-1">Sonuç belgesi onaylandığında bu adrese e-posta gönderilir.</p>
                </div>
              )}
              {form.role === "student" && (
                <>
                  <div>
                    <label className="ta-label">Sınıf</label>
                    <input className="ta-input" value={form.class_no || ""} onChange={(e) => setForm({ ...form, class_no: e.target.value })} placeholder="Örn: 12" data-testid="uf-class" />
                  </div>
                  <div>
                    <label className="ta-label">Şube</label>
                    <input className="ta-input" value={form.sube || ""} onChange={(e) => setForm({ ...form, sube: e.target.value })} placeholder="Örn: A" data-testid="uf-sube" />
                  </div>
                  <div>
                    <label className="ta-label">İl</label>
                    <input className="ta-input" value={form.il || ""} onChange={(e) => setForm({ ...form, il: e.target.value })} placeholder="Örn: İstanbul" data-testid="uf-il" />
                  </div>
                  <div>
                    <label className="ta-label">İlçe</label>
                    <input className="ta-input" value={form.ilce || ""} onChange={(e) => setForm({ ...form, ilce: e.target.value })} placeholder="Örn: Kadıköy" data-testid="uf-ilce" />
                  </div>
                  <div className="col-span-2">
                    <label className="ta-label">Sınıf / Alan</label>
                    {tracks.filter((t) => t.includes("Sınıf")).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="uf-track-chips">
                        {tracks.filter((t) => t.includes("Sınıf")).map((t) => (
                          <button type="button" key={t} onClick={() => setForm({ ...form, track: t })} data-testid={`uf-track-chip-${t}`}
                            className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${form.track === t ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>{t}</button>
                        ))}
                      </div>
                    )}
                    <select className="ta-input" value={form.track || ""} onChange={(e) => setForm({ ...form, track: e.target.value })} data-testid="uf-track">
                      <option value="">Seçiniz</option>
                      {tracks.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="ta-label">Koç</label>
                    <select className="ta-input" value={form.coach_id || ""} onChange={(e) => setForm({ ...form, coach_id: e.target.value })} data-testid="uf-coach">
                      <option value="">Yok</option>
                      {coaches.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="ta-label">Veli</label>
                    <select className="ta-input" value={form.parent_id || ""} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} data-testid="uf-parent">
                      <option value="">Yok</option>
                      {parents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                </>
              )}
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="uf-save">
              {saving ? <Loader2 className="animate-spin" size={16} /> : editing ? "Güncelle" : "Oluştur"}
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}

const AI_SUGGESTIONS = [
  "Öğrencileri listele",
  "Elif Yılmaz'ın koçunu ata",
  "Hatalı girilen bir deneme kaydını düzelt",
  "Bir öğrencinin şifresini sıfırla",
];

function AiSection() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { role: "admin", text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/assistant", { message: text });
      setMsgs((m) => [...m, { role: "ai", text: data.reply, results: data.results }]);
    } catch (e2) {
      setMsgs((m) => [...m, { role: "ai", text: apiErr(e2), error: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="ai-section">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
          <Bot className="text-indigo-600" size={26} /> AI Sorun Giderici Asistan
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Sistemde kod değişikliği olmadan veri düzeltme, atama ve sorun giderme talimatları verin. Asistan yazdıklarınızı güvenli eylemlere çevirip uygular.
        </p>
      </div>

      {msgs.length === 0 && (
        <div className="flex flex-wrap gap-2">
          {AI_SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => setInput(s)} className="px-3 py-1.5 rounded-full border border-slate-300 text-xs text-slate-600 hover:border-indigo-400 hover:text-indigo-600" data-testid={`ai-suggestion-${s.slice(0, 12)}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3" data-testid="ai-messages">
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "admin" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
              m.role === "admin" ? "bg-indigo-600 text-white" : m.error ? "bg-rose-50 border border-rose-200 text-rose-700" : "bg-white border border-slate-200 text-slate-800"
            }`} data-testid={`ai-msg-${i}`}>
              {m.text}
              {m.results?.length > 0 && (
                <ul className="mt-2 pt-2 border-t border-slate-100 space-y-1 text-xs">
                  {m.results.map((r, j) => (
                    <li key={j} className="flex items-start gap-1.5" data-testid={`ai-result-${i}-${j}`}>
                      <span className="text-emerald-600 font-bold">✓</span> {r}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
        {busy && <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={15} /> Asistan çalışıyor...</div>}
      </div>

      <form onSubmit={send} className="flex gap-2">
        <input
          className="ta-input flex-1"
          placeholder="Örn: Elif Yılmaz'ın 2026-09-23 tarihli Kimya kaydındaki yanlış sayısını 5 yap"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          data-testid="ai-input"
        />
        <Button type="submit" disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-send">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
          <span className="ml-1">Gönder</span>
        </Button>
      </form>
    </div>
  );
}

const ENTRY_TABS = [
  { id: "schedule", label: "Haftalık Program", kind: "weekly-schedule", fields: [
    { name: "week", label: "Hafta", type: "number" },
    { name: "day", label: "Gün", type: "select", options: DAYS },
    { name: "time", label: "Saat", type: "text" },
    { name: "subject", label: "Ders", type: "select", options: ALL_SUBJECTS },
    { name: "resource_name", label: "Kaynak", type: "text" },
    { name: "unit_no", label: "Ünite No", type: "text" },
    { name: "unit_name", label: "Ünite / Konu", type: "text" },
    { name: "total", label: "Hedef Soru", type: "number" },
  ] },
  { id: "study", label: "Çalışma Programı", kind: "weekly-schedule", fields: [
    { name: "week", label: "Hafta", type: "number" },
    { name: "day", label: "Gün", type: "select", options: DAYS },
    { name: "subject", label: "Ders", type: "select", options: ALL_SUBJECTS },
    { name: "unit_name", label: "Ünite / Konu", type: "text" },
    { name: "total", label: "Hedef Soru", type: "number" },
    { name: "status", label: "Durum", type: "select", options: ["Bekliyor", "Tamamlandı", "Onaylı", "Yapılmadı"] },
  ] },
  { id: "daily", label: "Günlük Soru", kind: "daily-questions", fields: [
    { name: "date", label: "Tarih", type: "date" },
    { name: "subject", label: "Ders", type: "select", options: ALL_SUBJECTS },
    { name: "resource_name", label: "Kaynak", type: "text" },
    { name: "unit_no", label: "Ünite No", type: "text" },
    { name: "unit_name", label: "Ünite / Konu", type: "text" },
    { name: "total", label: "Toplam", type: "number" },
    { name: "correct", label: "Doğru", type: "number" },
    { name: "wrong", label: "Yanlış", type: "number" },
    { name: "blank", label: "Boş", type: "number" },
  ] },
  { id: "resources", label: "Kaynak Takibi", kind: "resource-sections", fields: [
    { name: "week", label: "Hafta", type: "number" },
    { name: "subject", label: "Ders", type: "select", options: ALL_SUBJECTS },
    { name: "resource_name", label: "Kaynak Adı", type: "text" },
    { name: "unit", label: "Bölüm / Ünite", type: "text" },
    { name: "pages", label: "Sayfa Aralığı", type: "text" },
    { name: "day", label: "Gün", type: "select", options: DAYS },
    { name: "total", label: "Toplam", type: "number" },
    { name: "correct", label: "Doğru", type: "number" },
    { name: "wrong", label: "Yanlış", type: "number" },
    { name: "blank", label: "Boş", type: "number" },
  ] },
  { id: "units", label: "Ünite / Konu", kind: "unit-progress", fields: [
    { name: "exam", label: "Sınav", type: "select", options: EXAM_TYPES },
    { name: "subject", label: "Ders", type: "select", options: ALL_SUBJECTS },
    { name: "unit_no", label: "Ünite No", type: "text" },
    { name: "unit_name", label: "Ünite / Konu", type: "text" },
    { name: "completion", label: "Tamamlanma %", type: "number" },
  ] },
  { id: "pppd", label: "PPD Takibi", kind: "pppd", fields: [
    { name: "date", label: "Tarih", type: "date" },
    { name: "type", label: "Tür", type: "select", options: PPPD_TYPES },
    { name: "category", label: "Kategori", type: "select", options: PPPD_CATEGORIES },
    { name: "name", label: "Deneme Adı", type: "text" },
    { name: "publisher", label: "Yayınevi", type: "text" },
    { name: "resource_name", label: "Kaynak", type: "text" },
    { name: "total", label: "Toplam", type: "number" },
    { name: "correct", label: "Doğru", type: "number" },
    { name: "wrong", label: "Yanlış", type: "number" },
    { name: "blank", label: "Boş", type: "number" },
  ] },
  { id: "weak", label: "Zayıf Konular", kind: "study-cards", fields: [
    { name: "subject", label: "Ders", type: "select", options: ALL_SUBJECTS },
    { name: "unit", label: "Konu / Ünite", type: "text" },
    { name: "error_rate", label: "Hata Oranı %", type: "number" },
    { name: "note", label: "Not", type: "text" },
  ] },
  { id: "branch", label: "Branş Deneme", kind: "branch-exams", fields: [
    { name: "date", label: "Tarih", type: "date" },
    { name: "branch", label: "Branş", type: "select", options: BRANCHES },
    { name: "name", label: "Deneme Adı", type: "text" },
    { name: "publisher", label: "Yayınevi", type: "text" },
    { name: "total", label: "Toplam Soru", type: "number" },
    { name: "correct", label: "Doğru", type: "number" },
    { name: "wrong", label: "Yanlış", type: "number" },
    { name: "blank", label: "Boş", type: "number" },
  ] },
  { id: "general", label: "Genel Deneme", kind: "general-exams", fields: [
    { name: "date", label: "Tarih", type: "date" },
    { name: "exam_type", label: "Puan Türü", type: "select", options: EXAM_TYPES },
    { name: "name", label: "Deneme Adı", type: "text" },
    { name: "publisher", label: "Yayınevi", type: "text" },
    { name: "total_net", label: "Toplam Net", type: "number" },
    { name: "ranking", label: "Sıralama", type: "number" },
  ] },
  { id: "targets", label: "Hedef Net", kind: "targets", fields: [
    { name: "university", label: "Üniversite", type: "text" },
    { name: "department", label: "Bölüm", type: "text" },
    { name: "target_tyt_net", label: "Hedef TYT Net", type: "number" },
    { name: "target_ayt_net", label: "Hedef AYT Net", type: "number" },
  ] },
];

const NUM_FIELDS = ["week", "total", "correct", "wrong", "blank", "completion", "total_net", "ranking", "error_rate", "target_tyt_net", "target_ayt_net"];

function StudentDataEntry({ module }) {
  const [students, setStudents] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [sid, setSid] = useState("");
  const [targetType, setTargetType] = useState("student"); // student | track
  const [tracksSel, setTracksSel] = useState([]); // en fazla 3 sınıf/alan
  const [studentsSel, setStudentsSel] = useState([]); // toplu atamada ekstra öğrenciler (çoklu)

  const toggleEntryStudent = (id) =>
    setStudentsSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const targetStudentCount = () => {
    const ids = new Set(students.filter((s) => tracksSel.includes(s.track)).map((s) => s.id));
    studentsSel.forEach((id) => ids.add(id));
    return ids.size;
  };
  const tab = module;
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/meta").then((r) => setTracks(r.data.tracks || [])).catch(() => {});
    api.get("/program-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const active = ENTRY_TABS.find((t) => t.id === tab);
  const WS_TAB = { study: "study-program", daily: "daily", resources: "resources", units: "units", pppd: "pppd", branch: "branch", general: "general", weak: "weak", targets: "targets" };
  const selStudent = students.find((s) => s.id === sid);

  useEffect(() => { setForm({}); }, [tab]);

  const setF = (n, v) => setForm((f) => ({ ...f, [n]: v }));

  const toggleTrack = (t) => {
    setTracksSel((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return prev;
      }
      return [...prev, t];
    });
  };

  const save = async (keepForm = false, extra = {}) => {
    if (!sid) return toast.error("Önce öğrenci seçin");
    const req = active.fields.find((f) => f.type === "date") || active.fields.find((f) => f.name === "subject");
    const src = { ...form, ...extra };
    if (req && !src[req.name]) return toast.error(`${req.label} zorunlu`);
    setBusy(true);
    try {
      const payload = {};
      for (const f of active.fields) {
        const v = src[f.name];
        payload[f.name] = NUM_FIELDS.includes(f.name) ? Number(v || 0) : (v || "");
      }
      if ("correct" in payload || "wrong" in payload) {
        payload.net = Math.round((Number(payload.correct || 0) - Number(payload.wrong || 0) / 4) * 100) / 100;
      }
      if (active.kind === "weekly-schedule" && !payload.status) payload.status = "Bekliyor";
      await api.post(`/${active.kind}/${sid}`, payload);
      toast.success(keepForm ? "Deneme eklendi — form açık kaldı" : "Kaydedildi — öğrenci panelinde görünür");
      if (!keepForm) setForm({});
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const DENEME_PRESET = {
    pppd: { category: "Deneme" },
    daily: { resource_name: "Deneme" },
    branch: {},
    general: {},
  };

  const quickDeneme = () => {
    const preset = DENEME_PRESET[tab] || {};
    setForm((f) => ({ ...f, ...preset }));
    save(true, preset);
  };

  const [progress, setProgress] = useState(null);
  const openProgress = async () => {
    setProgress({ loading: true, items: [] });
    try {
      const { data } = await api.get(`/optical-results/${sid}`);
      setProgress({ loading: false, items: data });
    } catch (e) {
      toast.error(apiErr(e));
      setProgress(null);
    }
  };

  const applyTemplate = async () => {
    if (!tplId) return toast.error("Şablon seçin");
    if (targetType === "student" && !sid) return toast.error("Öğrenci seçin");
    if (targetType === "track" && tracksSel.length === 0) return toast.error("Sınıf / alan seçin");
    setBusy(true);
    try {
      const body = { target: targetType, start_week: Number(form.week || 1) };
      if (targetType === "track") body.tracks = tracksSel; else body.student_id = sid;
      const { data } = await api.post(`/program-templates/${tplId}/assign`, body);
      toast.success(`Şablon uygulandı: ${data.students} öğrenciye ${data.created} ders atandı`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const buildRow = () => {
    const row = {};
    for (const f of active.fields) row[f.name] = form[f.name] ?? "";
    if (active.kind === "resource-sections") {
      row.plan_name = form.resource_name || "";
      row.plan_type = "Genel";
      row.plan_weeks = 4;
    }
    return row;
  };

  const requiredField = () =>
    active.fields.find((f) => f.type === "date") ||
    active.fields.find((f) => f.name === "subject") ||
    active.fields.find((f) => f.name === "university");

  const assign = async (rowsOverride) => {
    const req = requiredField();
    if (!rowsOverride && req && !form[req.name]) return toast.error(`${req.label} zorunlu`);
    if (targetType === "student" && !sid) return toast.error("Öğrenci seçin");
    if (targetType === "track" && tracksSel.length === 0 && studentsSel.length === 0) return toast.error("Sınıf / alan veya öğrenci seçin");
    setBusy(true);
    try {
      const bulkKind = active.kind === "resource-sections" ? "resources" : active.kind;
      const body = { rows: rowsOverride || [buildRow()], target: targetType };
      if (targetType === "track") {
        body.tracks = tracksSel;
        body.student_ids = studentsSel;
      } else body.student_id = sid;
      const { data } = await api.post(`/bulk/assign/${bulkKind}`, body);
      toast.success(`${data.students} öğrenciye ${data.created} kayıt atandı`);
      if (data.errors?.length) toast.warning(`${data.errors.length} satır atlandı`);
      setForm({});
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  // Toplu Deneme Ekle — PPD/Branş panelindeki modalın birebir aynısı, tüm panellerde ortak
  const [bulk, setBulk] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkTargets, setBulkTargets] = useState({ tracks: [], students: [] });
  const BULK_TYPE = {
    study: { field: "subject", label: "Ders", options: ALL_SUBJECTS },
    daily: { field: "subject", label: "Ders", options: ALL_SUBJECTS },
    resources: { field: "subject", label: "Ders", options: ALL_SUBJECTS },
    units: { field: "subject", label: "Ders", options: ALL_SUBJECTS },
    pppd: { field: "type", label: "Tür", options: PPPD_TYPES },
    weak: { field: "subject", label: "Ders", options: ALL_SUBJECTS },
    branch: { field: "branch", label: "Branş", options: BRANCHES },
    general: { field: "exam_type", label: "Puan Türü", options: EXAM_TYPES },
    targets: null,
  };
  const openBulk = () => {
    const cfg = BULK_TYPE[tab];
    setBulk({ name: "", publisher: "", type: cfg ? cfg.options[0] : "", count: 10, qcount: "", date: "", optik: false, branches: {} });
    setBulkTargets({ tracks: [], students: [] });
  };

  const toggleBulkBranch = (b) =>
    setBulk((p) => {
      const branches = { ...(p.branches || {}) };
      if (b in branches) delete branches[b];
      else branches[b] = BRANCH_QCOUNTS[b] || 40;
      return { ...p, branches };
    });

  const setBranchQ = (b, v) => setBulk((p) => ({ ...p, branches: { ...(p.branches || {}), [b]: v } }));

  const toggleBulkTrack = (t) =>
    setBulkTargets((p) => {
      if (p.tracks.includes(t)) return { ...p, tracks: p.tracks.filter((x) => x !== t) };
      if (p.tracks.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return p;
      }
      return { ...p, tracks: [...p.tracks, t] };
    });

  const toggleBulkStudent = (id) =>
    setBulkTargets((p) => ({ ...p, students: p.students.includes(id) ? p.students.filter((x) => x !== id) : [...p.students, id] }));

  const bulkStudentIds = () => {
    const ids = new Set(bulkTargets.students);
    students.forEach((s) => { if (bulkTargets.tracks.includes(s.track)) ids.add(s.id); });
    if (!ids.size && targetType === "student" && sid) ids.add(sid);
    return [...ids];
  };

  const fmtDate = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;

  const mapBulkRow = ({ date, name, publisher, type, total, correct, wrong, blank }, i) => {
    switch (tab) {
      case "pppd": return { date, type, category: "Deneme", name, publisher, resource_name: bulk.name.trim() || name, total, correct, wrong, blank };
      case "branch": return { date, branch: type, name, publisher, total, correct, wrong, blank };
      case "general": return { date, exam_type: type, name, publisher, total_net: 0, ranking: 0 };
      case "daily": return { date, subject: type, resource_name: name, unit_name: bulk.name.trim(), total, correct, wrong, blank };
      case "resources": return { week: i + 1, subject: type, resource_name: name, unit: "", pages: "", day: "", total, correct, wrong, blank };
      case "units": return { exam: "", subject: type, unit_no: String(i + 1), unit_name: name, completion: 0 };
      case "study": return { week: i + 1, day: "", subject: type, unit_name: name, total, status: "Bekliyor" };
      case "weak": return { subject: type, unit: name, error_rate: total, note: publisher };
      case "targets": return { university: name, department: publisher, target_tyt_net: total, target_ayt_net: 0 };
      default: return { name };
    }
  };

  const saveBulk = async () => {
    const n = Number(bulk.count || 0);
    if (!bulk.name.trim()) return toast.error("Deneme adı zorunlu");
    if (n < 1 || n > 200) return toast.error("Adet 1-200 arası olmalı");
    const isBranch = tab === "branch";
    const brs = isBranch ? Object.entries(bulk.branches || {}) : [];
    if (isBranch && !brs.length) return toast.error("En az bir branş seçin");
    const ids = bulkStudentIds();
    if (!ids.length) return toast.error("Hedef öğrenci veya sınıf/alan seçin");
    setBulkSaving(true);
    try {
      const qc = Number(bulk.qcount) || 0;
      const base = bulk.date ? new Date(bulk.date + "T00:00:00") : null;
      const dateAt = (i) => (base ? fmtDate(new Date(base.getTime() + i * 86400000)) : "");
      const rows = [];
      if (isBranch) {
        for (let i = 0; i < n; i++) {
          for (const [b, q] of brs) {
            const bq = Number(q) || 0;
            rows.push({ date: dateAt(i), branch: b, name: `${bulk.name.trim()} ${i + 1}`, publisher: bulk.publisher.trim(), total: bq, correct: 0, wrong: 0, blank: bq });
          }
        }
      } else {
        for (let i = 0; i < n; i++) {
          rows.push(mapBulkRow({ date: dateAt(i), name: `${bulk.name.trim()} ${i + 1}`, publisher: bulk.publisher.trim(), type: bulk.type, total: qc, correct: 0, wrong: 0, blank: qc }, i));
        }
      }
      const { data } = await api.post(`/records-bulk/${active.kind}/${ids[0]}`, { rows, student_ids: ids });
      toast.success(`${data.created} kayıt ${data.students} öğrenciye oluşturuldu${isBranch ? ` (${brs.length} branş × ${n} adet)` : ""} — sonuçları öğrenci panelinde düzenle`);
      if (bulk.optik) {
        if (isBranch) {
          for (let i = 0; i < n; i++) {
            await api.post("/optical-exams", {
              name: `${bulk.name.trim()} ${i + 1}`, exam_type: "Branş Deneme", date: dateAt(i),
              subjects: brs.map(([b, q]) => ({ name: b, question_count: Math.max(1, Number(q) || 1), answer_key: "", kazanimlar: [] })),
              assigned_tracks: bulkTargets.tracks, assigned_student_ids: ids,
            });
          }
        } else {
          const q = Math.max(1, qc);
          for (const r of rows) {
            await api.post("/optical-exams", {
              name: r.name || r.unit_name || r.resource_name || r.university, exam_type: "Branş Deneme", date: r.date || "",
              subjects: [{ name: bulk.type, question_count: q, answer_key: "", kazanimlar: [] }],
              assigned_tracks: bulkTargets.tracks, assigned_student_ids: ids,
            });
          }
        }
        toast.success("Canlı optik sınavlar da oluşturuldu — cevap anahtarları sonra girilebilir");
      }
      setBulk(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBulkSaving(false);
    }
  };

  const uploadBulkFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ids = bulkStudentIds();
    if (!ids.length) return toast.error("Hedef öğrenci veya sınıf/alan seçin");
    setBulkSaving(true);
    try {
      const text = await file.text();
      const isBranch = tab === "branch";
      const rows = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          if (isBranch) {
            const [date, br, name, publisher, total, correct, wrong, blank] = l.split(";").map((x) => (x || "").trim());
            return { date: date || "", branch: br || "", name, publisher: publisher || "", total: Number(total) || 0, correct: Number(correct) || 0, wrong: Number(wrong) || 0, blank: Number(blank) || 0 };
          }
          const [date, name, publisher, total, correct, wrong, blank] = l.split(";").map((x) => (x || "").trim());
          return { date: date || "", name, publisher: publisher || "", total: Number(total) || 0, correct: Number(correct) || 0, wrong: Number(wrong) || 0, blank: Number(blank) || 0 };
        })
        .filter((r) => r.name && (!isBranch || r.branch))
        .map((r, i) => (isBranch ? r : mapBulkRow({ ...r, type: bulk.type }, i)));
      if (!rows.length) {
        toast.error("Dosyada geçerli satır yok");
        return;
      }
      const { data } = await api.post(`/records-bulk/${active.kind}/${ids[0]}`, { rows, student_ids: ids });
      toast.success(`${data.created} kayıt ${data.students} öğrenciye dosyadan yüklendi`);
      setBulk(null);
    } catch (e2) {
      toast.error(e2.message || apiErr(e2));
    } finally {
      setBulkSaving(false);
    }
  };

  // Toplu Sonuç Gir — PPD panelindeki ile aynı, sonuç bekleyen kayıtlar için
  const [resOpen, setResOpen] = useState(null);
  const [resSaving, setResSaving] = useState(false);
  const IS_GENERAL = tab === "general";
  const HAS_RESULT_ENTRY = ["daily", "resources", "pppd", "branch", "general"].includes(tab);

  const openResultEntry = async () => {
    if (targetType !== "student" || !sid) return toast.error("Önce tek öğrenci seçin");
    setBusy(true);
    try {
      const { data } = await api.get(`/${active.kind}/${sid}`);
      const rows = IS_GENERAL
        ? data.filter((r) => !Number(r.total_net)).map((r) => ({ ...r, tn: "", rk: "" }))
        : data.filter((r) => !Number(r.correct) && !Number(r.wrong)).map((r) => ({ ...r, t: "", dc: "", yc: "", bl: "" }));
      if (!rows.length) {
        toast.info("Sonuç bekleyen kayıt yok");
        return;
      }
      setResOpen(rows);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveResults = async () => {
    const updates = IS_GENERAL
      ? resOpen
          .filter((r) => r.tn !== "" || r.rk !== "")
          .map((r) => ({ id: r.id, total_net: Number(r.tn || 0), ranking: Number(r.rk || 0) }))
      : resOpen
          .filter((r) => r.dc !== "" || r.yc !== "" || r.bl !== "")
          .map((r) => ({
            id: r.id,
            total: Number(r.t || 0) || Number(r.dc || 0) + Number(r.yc || 0) + Number(r.bl || 0),
            correct: Number(r.dc || 0),
            wrong: Number(r.yc || 0),
            blank: Number(r.bl || 0),
          }));
    if (!updates.length) return toast.error("En az bir satır doldurun");
    setResSaving(true);
    try {
      const { data } = await api.put(`/records-bulk/${active.kind}/${sid}`, { updates });
      toast.success(IS_GENERAL ? `${data.updated} deneme sonucu kaydedildi` : `${data.updated} kayıt sonucu kaydedildi — net otomatik hesaplandı`);
      setResOpen(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setResSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="entry-section">
      <div>
        <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><FileUp size={13} /> Manuel Veri Girişi</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mt-1">{active.label}</h1>
        <p className="text-slate-500 text-sm mt-1">Bu veriyi öğrenci adına el ile girin veya sınıf/alana toplu atayın.</p>
      </div>

      <div className="card-ta p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 shrink-0"><Users size={16} /> Hedef</span>
          {targetType === "student" && sid && ["pppd", "branch", "general"].includes(tab) && (
            <Button variant="outline" onClick={openProgress} className="border-emerald-300 text-emerald-700 h-9" data-testid="entry-progress">
              <TrendingUp size={15} /><span className="ml-1">İlerleme</span>
            </Button>
          )}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="target-type">
            <button onClick={() => setTargetType("student")} data-testid="target-student"
              className={`px-3.5 py-1.5 text-sm font-semibold transition-colors ${targetType === "student" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              Tek Öğrenci
            </button>
            <button onClick={() => setTargetType("track")} data-testid="target-track"
              className={`px-3.5 py-1.5 text-sm font-semibold transition-colors ${targetType === "track" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
              Sınıf / Alan (Toplu)
            </button>
          </div>
        </div>
        {targetType === "student" ? (
          <select className="ta-input max-w-md" value={sid} onChange={(e) => setSid(e.target.value)} data-testid="entry-student">
            <option value="">Öğrenci seç</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name}{s.track ? ` · ${s.track}` : ""}</option>)}
          </select>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5" data-testid="entry-track-chips">
              {tracks.map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => toggleTrack(t)}
                  data-testid={`entry-track-chip-${t}`}
                  className={`text-xs px-2.5 py-1.5 rounded-full border font-semibold transition-colors ${
                    tracksSel.includes(t) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="text-xs text-slate-400">En fazla 3 sınıf/alan seçebilirsiniz ({tracksSel.length}/3)</div>
            <div>
              <div className="text-xs font-bold text-slate-500 mb-1">EKSTRA ÖĞRENCİ (çoklu — ops.)</div>
              <div className="max-h-28 overflow-y-auto grid sm:grid-cols-2 gap-1 bg-white border border-slate-200 rounded-lg p-2" data-testid="entry-student-checks">
                {students
                  .filter((s) => !tracksSel.length || tracksSel.includes(s.track))
                  .map((s) => (
                    <label key={s.id} className="flex items-center gap-1.5 text-xs text-slate-700 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`entry-stu-${s.id}`}>
                      <input type="checkbox" className="accent-emerald-600" checked={studentsSel.includes(s.id)} onChange={() => toggleEntryStudent(s.id)} />
                      {s.name} {s.track ? <span className="text-slate-400">· {s.track}</span> : null}
                    </label>
                  ))}
              </div>
            </div>
          </div>
        )}
        {targetType === "track" && (tracksSel.length > 0 || studentsSel.length > 0) && (
          <div className="text-xs text-slate-500" data-testid="track-count">
            {targetStudentCount()} öğrenciye atanacak
          </div>
        )}
      </div>

      {tab === "schedule" && templates.length > 0 && (
        <div className="card-ta p-4 flex flex-wrap items-center gap-3" data-testid="entry-template-box">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 shrink-0"><CalendarCheck size={16} /> Şablondan Ata</span>
          <select className="ta-input max-w-xs" value={tplId} onChange={(e) => setTplId(e.target.value)} data-testid="entry-template">
            <option value="">Program şablonu seç</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.weeks} hafta</option>)}
          </select>
          <Button onClick={applyTemplate} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-10" data-testid="entry-template-assign">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}<span className="ml-1">Seçili Hedefe Uygula</span>
          </Button>
          <span className="text-xs text-slate-400">Yukarıdaki hedef (öğrenci/alan) kullanılır; başlangıç haftası formdaki "Hafta" alanından alınır.</span>
        </div>
      )}

      <div className="card-ta p-5">
        <datalist id="entry-etut-options">{ETUT_OPTIONS.map((o) => <option key={o} value={o} />)}</datalist>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {active.fields.map((f) => (
            <div key={f.name}>
              <label className="ta-label">{f.label}</label>
              {f.type === "select" ? (
                <select className="ta-input" value={form[f.name] || ""} onChange={(e) => setF(f.name, e.target.value)} data-testid={`entry-${f.name}`}>
                  <option value="">Seçiniz</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type} list={f.name === "time" ? "entry-etut-options" : undefined} className="ta-input" value={form[f.name] || ""} placeholder={f.type === "number" ? "0" : f.name === "time" ? "14:00 veya 1. Etüt" : ""} onChange={(e) => setF(f.name, e.target.value)} data-testid={`entry-${f.name}`} />
              )}
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={() => save()} disabled={busy || targetType !== "student"} className="bg-indigo-900 hover:bg-indigo-800" data-testid="entry-save">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}<span className="ml-1">Kaydet</span>
          </Button>
          <Button onClick={quickDeneme} disabled={busy || targetType !== "student"} className="bg-amber-500 hover:bg-amber-600" data-testid="entry-deneme-add">
            <Plus size={16} /><span className="ml-1">Deneme Ekle</span>
          </Button>
          <Button onClick={() => assign()} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="entry-assign">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}<span className="ml-1">Panele Ata</span>
          </Button>
          <Button onClick={openBulk} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="entry-bulk-btn">
            <Layers size={16} /><span className="ml-1">Toplu Deneme Ekle</span>
          </Button>
          {HAS_RESULT_ENTRY && (
            <Button onClick={openResultEntry} disabled={busy || targetType !== "student" || !sid} className="bg-emerald-600 hover:bg-emerald-700" data-testid="entry-results-btn">
              <ListChecks size={16} /><span className="ml-1">Toplu Sonuç Gir</span>
            </Button>
          )}
        </div>
        {targetType === "track" && (
          <p className="text-xs text-slate-500 mt-2">"Panele Ata", girilen kaydı seçili sınıf/alandaki tüm öğrencilere toplu atar.</p>
        )}
      </div>

      {bulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setBulk(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="entry-bulk-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><Layers size={18} className="text-violet-600" /> Toplu Deneme Ekle</h3>
              <button onClick={() => setBulk(null)} className="text-slate-400 hover:text-slate-700" data-testid="entry-bulk-close"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Örn: "3D Dilbilgisi Denemesi" + Adet 30 → listede <b>3D Dilbilgisi Denemesi 1…30</b> oluşur, sonuçları tek tek düzenlersin. Tarih girersen kayıtlar <b>art arda günlere</b> otomatik dağıtılır (1. kayıt seçilen gün, sonrakiler ertesi günler).</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="ta-label">Deneme Adı</label>
                <input className="ta-input" value={bulk.name} onChange={(e) => setBulk({ ...bulk, name: e.target.value })} data-testid="entry-bulk-name" placeholder="Örn: 3D Dilbilgisi Denemesi" />
              </div>
              <div>
                <label className="ta-label">Yayınevi</label>
                <input className="ta-input" value={bulk.publisher} onChange={(e) => setBulk({ ...bulk, publisher: e.target.value })} data-testid="entry-bulk-publisher" placeholder="Örn: 3D Yayınları" />
              </div>
              {tab === "branch" ? (
                <div className="col-span-2">
                  <label className="ta-label">Branşlar — birden fazla seçin; soru sayısı otomatik gelir, değiştirebilirsiniz</label>
                  <div className="flex flex-wrap gap-1.5">
                    {BRANCHES.map((b) => {
                      const sel = bulk.branches && b in bulk.branches;
                      return sel ? (
                        <span key={b} className="flex items-center gap-1 rounded-full border border-violet-600 bg-violet-600 text-white pl-2.5 pr-1 py-0.5">
                          <button type="button" onClick={() => toggleBulkBranch(b)} className="text-xs font-semibold" data-testid={`entry-bulk-branch-${b}`}>{b} ✕</button>
                          <input type="number" min="0" value={bulk.branches[b]} onChange={(e) => setBranchQ(b, e.target.value)} className="w-12 text-xs text-center rounded bg-white text-slate-800 py-0.5" data-testid={`entry-bulk-branchq-${b}`} />
                        </span>
                      ) : (
                        <button key={b} type="button" onClick={() => toggleBulkBranch(b)} className="text-xs px-2.5 py-1 rounded-full border font-semibold bg-white text-slate-600 border-slate-200 hover:bg-slate-100" data-testid={`entry-bulk-branch-${b}`}>{b}</button>
                      );
                    })}
                  </div>
                </div>
              ) : BULK_TYPE[tab] ? (
                <div>
                  <label className="ta-label">{BULK_TYPE[tab].label}</label>
                  <select className="ta-input" value={bulk.type} onChange={(e) => setBulk({ ...bulk, type: e.target.value })} data-testid="entry-bulk-type">
                    {BULK_TYPE[tab].options.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              ) : (
                <div />
              )}
              <div>
                <label className="ta-label">Adet</label>
                <input type="number" min="1" max="200" className="ta-input" value={bulk.count} onChange={(e) => setBulk({ ...bulk, count: e.target.value })} data-testid="entry-bulk-count" />
              </div>
              {tab !== "branch" && (
                <div>
                  <label className="ta-label">Soru Sayısı</label>
                  <input type="number" min="0" className="ta-input text-center" value={bulk.qcount} onChange={(e) => setBulk({ ...bulk, qcount: e.target.value })} data-testid="entry-bulk-qcount" />
                </div>
              )}
              <div>
                <label className="ta-label">Başlangıç Tarihi (ops. — art arda günlere dağıtılır)</label>
                <input type="date" className="ta-input" value={bulk.date} onChange={(e) => setBulk({ ...bulk, date: e.target.value })} data-testid="entry-bulk-date" />
              </div>
              {students.length > 0 && (
                <div className="col-span-2 border-t border-slate-100 pt-3 space-y-2" data-testid="entry-bulk-targets">
                  <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                    <input type="checkbox" id="entry-optik" className="accent-indigo-700" checked={bulk.optik} onChange={(e) => setBulk({ ...bulk, optik: e.target.checked })} data-testid="entry-bulk-optik" />
                    <label htmlFor="entry-optik" className="text-xs text-indigo-800 font-semibold">Canlı Optik sınavı da oluştur (zorunlu değil — soru sayısı yukarıdan alınır, cevap anahtarı sonra girilebilir)</label>
                  </div>
                  <label className="ta-label">Hedef — birden fazla sınıf/alan ve öğrenci seçebilirsiniz (varsayılan: seçili öğrenci)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {tracks.map((t) => (
                      <button
                        type="button"
                        key={t}
                        onClick={() => toggleBulkTrack(t)}
                        data-testid={`entry-bulk-track-${t}`}
                        className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${
                          bulkTargets.tracks.includes(t) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-28 overflow-y-auto grid grid-cols-2 gap-1 bg-white border border-slate-200 rounded-lg p-2">
                    {students
                      .filter((s) => !bulkTargets.tracks.length || bulkTargets.tracks.includes(s.track))
                      .map((s) => (
                        <label key={s.id} className="flex items-center gap-1.5 text-xs text-slate-700 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`entry-bulk-stu-${s.id}`}>
                          <input type="checkbox" className="accent-violet-600" checked={bulkTargets.students.includes(s.id)} onChange={() => toggleBulkStudent(s.id)} />
                          {s.name} {s.track ? <span className="text-slate-400">· {s.track}</span> : null}
                        </label>
                      ))}
                  </div>
                  <div className="text-[11px] text-violet-600 font-semibold" data-testid="entry-bulk-count-info">
                    {bulkStudentIds().length} öğrenciye eklenecek
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <label className="ta-label">Veya Dosya ile Toplu Yükle (.csv / .txt)</label>
              <p className="text-[11px] text-slate-400 mb-2">
                Her satır: <code className="bg-slate-50 px-1 rounded border border-slate-200 font-mono">{tab === "branch" ? "tarih;branş;deneme_adı;yayınevi;toplam;doğru;yanlış;boş" : "tarih;deneme_adı;yayınevi;toplam;doğru;yanlış;boş"}</code>
              </p>
              <input type="file" accept=".csv,.txt" onChange={uploadBulkFile} disabled={bulkSaving} className="text-xs text-slate-600" data-testid="entry-bulk-file" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulk(null)}>Vazgeç</Button>
              <Button onClick={saveBulk} disabled={bulkSaving} className="bg-violet-600 hover:bg-violet-700" data-testid="entry-bulk-save">
                {bulkSaving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}<span className="ml-1">Oluştur</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {resOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setResOpen(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="entry-results-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold flex items-center gap-2"><ListChecks size={18} className="text-emerald-600" /> Toplu Sonuç Gir (elle){selStudent ? ` — ${selStudent.name}` : ""}</h3>
              <button onClick={() => setResOpen(null)} className="text-slate-400 hover:text-slate-700" data-testid="entry-results-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              {IS_GENERAL
                ? "Sonuç bekleyen denemeler listelenir. Toplam net ve sıralama girilir; tüm panellerde görünür."
                : "Sonuç bekleyen kayıtlar listelenir. Toplam boş bırakılırsa D+Y+B toplamı alınır; net otomatik hesaplanır ve tüm panellerde görünür."}
            </p>
            {IS_GENERAL ? (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_96px_96px] gap-2 text-[11px] font-bold text-slate-400 uppercase px-1">
                  <span>Deneme</span><span className="text-center">Toplam Net</span><span className="text-center">Sıralama</span>
                </div>
                {resOpen.map((r) => (
                  <div key={r.id} className="grid grid-cols-[1fr_96px_96px] gap-2 items-center bg-slate-50 rounded-lg px-2 py-1.5">
                    <div className="text-xs font-semibold text-slate-700 truncate">{r.name}<span className="text-slate-400 font-normal"> · {r.date || "-"}</span></div>
                    <input type="number" step="0.01" className="ta-input !py-1 !text-xs text-center border-indigo-200" value={r.tn} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, tn: e.target.value } : x)))} data-testid={`res-tn-${r.id}`} />
                    <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.rk} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, rk: e.target.value } : x)))} data-testid={`res-rk-${r.id}`} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_64px_64px_64px_64px] gap-2 text-[11px] font-bold text-slate-400 uppercase px-1">
                  <span>Kayıt</span><span className="text-center">Toplam</span><span className="text-center">D</span><span className="text-center">Y</span><span className="text-center">Boş</span>
                </div>
                {resOpen.map((r) => (
                  <div key={r.id} className="grid grid-cols-[1fr_64px_64px_64px_64px] gap-2 items-center bg-slate-50 rounded-lg px-2 py-1.5">
                    <div className="text-xs font-semibold text-slate-700 truncate">
                      {r.name || r.resource_name || r.unit_name || "Kayıt"}
                      <span className="text-slate-400 font-normal"> · {r.date || (r.week ? `${r.week}. Hafta` : "-")}</span>
                    </div>
                    <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.t} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, t: e.target.value } : x)))} data-testid={`res-t-${r.id}`} />
                    <input type="number" className="ta-input !py-1 !text-xs text-center border-emerald-200" value={r.dc} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, dc: e.target.value } : x)))} data-testid={`res-d-${r.id}`} />
                    <input type="number" className="ta-input !py-1 !text-xs text-center border-rose-200" value={r.yc} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, yc: e.target.value } : x)))} data-testid={`res-y-${r.id}`} />
                    <input type="number" className="ta-input !py-1 !text-xs text-center" value={r.bl} onChange={(e) => setResOpen(resOpen.map((x) => (x.id === r.id ? { ...x, bl: e.target.value } : x)))} data-testid={`res-b-${r.id}`} />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResOpen(null)}>Vazgeç</Button>
              <Button onClick={saveResults} disabled={resSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="entry-results-save">
                {resSaving ? <Loader2 className="animate-spin" size={15} /> : <ListChecks size={15} />}<span className="ml-1">Sonuçları Kaydet</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {targetType === "student" && sid && WS_TAB[tab] && (
        <div className="card-ta p-5" data-testid="entry-workspace">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
            <LayoutDashboard size={16} className="text-indigo-600" />
            {selStudent?.name} · {active.label} — kayıt listesi (alt alta ekle, düzelt, sil)
          </div>
          <StudentWorkspace studentId={sid} studentName={selStudent?.name} track={selStudent?.track} readOnly={false} tab={WS_TAB[tab]} />
        </div>
      )}

      {progress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setProgress(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="progress-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold flex items-center gap-2">
                <TrendingUp size={18} className="text-emerald-600" /> Canlı Sınav (Optik) İlerlemesi
              </h3>
              <button onClick={() => setProgress(null)} className="text-slate-400 hover:text-slate-700" data-testid="progress-close"><X size={18} /></button>
            </div>
            {progress.loading ? (
              <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
            ) : progress.items.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-sm">Bu öğrencinin canlı (optik) sınav sonucu henüz yok. "Canlı Sınav (Optik)" menüsünden sonuç girildiğinde burada ve Branş/Genel Deneme panellerinde otomatik görünür.</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {(() => {
                    const nets = progress.items.map((i) => Number(i.totals?.net || 0));
                    const last = nets[0] || 0;
                    const prev = nets[1];
                    const avg = nets.reduce((a, b) => a + b, 0) / nets.length;
                    const up = prev != null ? last >= prev : null;
                    return (
                      <>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 text-center">
                          <div className="text-[11px] font-semibold uppercase text-slate-400">Sınav Sayısı</div>
                          <div className="font-display text-xl font-extrabold text-slate-900">{progress.items.length}</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 text-center">
                          <div className="text-[11px] font-semibold uppercase text-slate-400">Son Net</div>
                          <div className={`font-display text-xl font-extrabold ${up === null ? "text-slate-900" : up ? "text-emerald-600" : "text-rose-600"}`}>
                            {last.toFixed(2)} {up !== null && (up ? "▲" : "▼")}
                          </div>
                        </div>
                        <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 text-center">
                          <div className="text-[11px] font-semibold uppercase text-slate-400">Ortalama Net</div>
                          <div className="font-display text-xl font-extrabold text-indigo-700">{avg.toFixed(2)}</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500">
                      <th className="px-2 py-2 font-medium">Tarih</th>
                      <th className="px-2 py-2 font-medium">Sınav</th>
                      <th className="px-2 py-2 font-medium">Tür</th>
                      <th className="px-2 py-2 font-medium text-right">D</th>
                      <th className="px-2 py-2 font-medium text-right">Y</th>
                      <th className="px-2 py-2 font-medium text-right">B</th>
                      <th className="px-2 py-2 font-medium text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progress.items.map((i) => (
                      <tr key={i.id} className="border-b border-slate-100">
                        <td className="px-2 py-2 text-slate-600">{i.date || "-"}</td>
                        <td className="px-2 py-2 font-medium text-slate-800">{i.exam_name}</td>
                        <td className="px-2 py-2"><span className="text-[11px] px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200">{i.exam_type}</span></td>
                        <td className="px-2 py-2 text-right text-emerald-700 font-semibold">{i.totals?.dc ?? 0}</td>
                        <td className="px-2 py-2 text-right text-rose-700 font-semibold">{i.totals?.yc ?? 0}</td>
                        <td className="px-2 py-2 text-right text-slate-500">{i.totals?.blank ?? 0}</td>
                        <td className="px-2 py-2 text-right font-mono font-semibold text-indigo-700">{Number(i.totals?.net || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function isoWeek(d) {
  const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
  return `${dt.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

const MEDAL = {
  1: "bg-amber-400 text-white",
  2: "bg-slate-300 text-slate-700",
  3: "bg-amber-700 text-white",
};

function RankingSection() {
  const [week, setWeek] = useState(isoWeek(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/analytics/weekly-ranking/${week}`);
      setData(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [week]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5" data-testid="ranking-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Haftalık Öğrenci Sıralaması</h1>
          <p className="text-slate-500 text-sm mt-1">
            Kayıtlı öğrencilerin günlük soru, genel deneme, branş deneme ve PPD toplamlarına göre haftalık sıralaması.
            {data && <span className="font-medium text-slate-700"> {data.start} → {data.end}</span>}
          </p>
        </div>
        <div>
          <label className="ta-label">Hafta Seç</label>
          <input type="week" className="ta-input" value={week} onChange={(e) => setWeek(e.target.value)} data-testid="ranking-week" />
        </div>
      </div>

      <div className="card-ta overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead>
              <tr className="text-left text-white" style={{ background: "#0B1B3F" }}>
                <th className="px-4 py-3 font-bold text-xs tracking-wider">SIRA</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider">ÖĞRENCİ</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider">ALAN</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right bg-emerald-700">GENEL TOPLAM SORU</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right">GÜNLÜK SORU</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right">DOĞRU</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right">YANLIŞ</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right">BOŞ</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right">NET</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right">GENEL DENEME</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right">BRANŞ DENEME</th>
                <th className="px-4 py-3 font-bold text-xs tracking-wider text-right">PPD</th>
              </tr>
            </thead>
            <tbody data-testid="ranking-table">
              {loading ? (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={20} /></td></tr>
              ) : !data || data.rows.length === 0 ? (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-500">Öğrenci kaydı yok.</td></tr>
              ) : (
                data.rows.map((r) => (
                  <tr key={r.student_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ranking-row-${r.student_id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-display font-extrabold text-sm ${MEDAL[r.rank] || "bg-slate-100 text-slate-500"}`} data-testid={`rank-${r.rank}`}>
                        {r.rank}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{r.name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{r.track || "-"}</td>
                    <td className="px-4 py-3 text-right font-display font-extrabold text-emerald-700 text-base bg-emerald-50" data-testid={`grand-total-${r.student_id}`}>{r.grand_total}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900">{r.q_total}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{r.q_correct}</td>
                    <td className="px-4 py-3 text-right text-rose-700 font-semibold">{r.q_wrong}</td>
                    <td className="px-4 py-3 text-right text-slate-500">{r.q_blank}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-indigo-700">{Number(r.q_net).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-xs text-slate-600 whitespace-nowrap">
                      {r.ge_count ? <span><b>{r.ge_count}</b> deneme · Ort <b className="text-emerald-700">{r.ge_avg_net}</b></span> : "-"}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-600 whitespace-nowrap">
                      {r.be_count ? <span><b>{r.be_count}</b> deneme · Net <b className="text-emerald-700">{r.be_net}</b></span> : "-"}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-slate-600 whitespace-nowrap">
                      {r.pppd_count ? <span><b>{r.pppd_count}</b> kayıt · <b>{r.pppd_total}</b> soru</span> : "-"}
                    </td>
                  </tr>
                ))
              )}
              {data && data.rows.length > 0 && (
                <tr className="bg-slate-100 font-bold border-t-2 border-slate-300" data-testid="ranking-total-row">
                  <td className="px-4 py-3" colSpan={3}>GENEL TOPLAM ({data.rows.length} öğrenci)</td>
                  <td className="px-4 py-3 text-right font-display text-emerald-700 text-base">{data.rows.reduce((a, r) => a + r.grand_total, 0)}</td>
                  <td className="px-4 py-3 text-right">{data.rows.reduce((a, r) => a + r.q_total, 0)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700">{data.rows.reduce((a, r) => a + r.q_correct, 0)}</td>
                  <td className="px-4 py-3 text-right text-rose-700">{data.rows.reduce((a, r) => a + r.q_wrong, 0)}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{data.rows.reduce((a, r) => a + r.q_blank, 0)}</td>
                  <td className="px-4 py-3 text-right">-</td>
                  <td className="px-4 py-3 text-right text-xs">{data.rows.reduce((a, r) => a + r.ge_count, 0)} deneme</td>
                  <td className="px-4 py-3 text-right text-xs">{data.rows.reduce((a, r) => a + r.be_count, 0)} deneme</td>
                  <td className="px-4 py-3 text-right text-xs">{data.rows.reduce((a, r) => a + r.pppd_total, 0)} soru</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


